import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { decodeJWT } from '../../../../shared/decodeJWT'
import NormalLayoutContainer from '../../../../shared/NormalLayoutContainer'
import { PageTitle } from '../../../../../components/page-title'
import { Spinner } from '../../../../shared/Spinner'
import notify from '../../../../../utilities/notify'
import useQuery from '../../../../../hooks/useQuery'
import QUERY_KEYS from '../../../../../graphql/queryKeys'
import usersQuery from '../../../../../graphql/queries/users.js'
import NotificationsReadOnlyList from '../../../../../components/notifications-read-only'
import {
  RECEIVE_HAULER_TEAM_MEMBER_ACTIVATE_FAILURE,
  RECEIVE_HAULER_TEAM_MEMBER_ACTIVATE_SUCCESS,
  RECEIVE_HAULER_TEAM_MEMBER_DEACTIVATE_SUCCESS,
  RECEIVE_HAULER_TEAM_MEMBER_DEACTIVATE_FAILURE,
  RECEIVE_CURRENT_HAULER_TEAM_MEMBER_NOTIFICATIONS_SUCCESS,
  RECEIVE_CURRENT_HAULER_TEAM_MEMBER_NOTIFICATIONS_FAILURE
} from '../../action/teamHaulerActions.js'
import HaulerTeamMemberDisplay from './components/HaulerTeamMemberDisplay'
import { useHistory, useParams } from 'react-router-dom'
import activateUser from '../../../../../graphql/mutations/activateUser.js'
import { camelCasedObjectToSnakeCasedObject } from '../../../../../utilities/randomUtilities.js'
import handleError from '../../../../../utilities/handleError.js'
import useMutation from '../../../../../hooks/useMutation.js'
import deactivateUser from '../../../../../graphql/mutations/deactivateUser.js'
import notificationsQuery from '../../../../../graphql/queries/notifications.js'
import { intercomConnection } from '../../../../../intercomConnection.js'

// eslint-disable-next-line camelcase
export default function HaulerTeam_IdContainer () {
  const dispatch = useDispatch()
  const history = useHistory()
  const { id } = useParams()
  const { user, dataLoaders } = useSelector(({ user: { user }, dataLoaders }) => ({ user, dataLoaders }))
  const decoded = decodeJWT()
  const selfUser = Number(id) === decoded.u

  const { data: { users }, isFetching: userListFetching, refetch: refetchUser } = useQuery([
    QUERY_KEYS.users,
    user.haulerId,
    null,
    null,
    null,
    null,
    [id]
  ],
  usersQuery,
  {
    enabled: Boolean(user.haulerId && !selfUser),
    onError () {
      notify('error', 'Unable to retrieve user')
    },
    placeholderData: { users: { nodes: [] } }
  })

  const { currentHaulerTeamMemberNotifications } =
    useSelector(({ currentHaulerTeamMemberNotifications }) => ({
      currentHaulerTeamMemberNotifications
    }))

  const { mutate: activateUserMutation } = useMutation(activateUser, {
    onSuccess (response) {
      response.user = camelCasedObjectToSnakeCasedObject(response.user)
      dispatch({ type: RECEIVE_HAULER_TEAM_MEMBER_ACTIVATE_SUCCESS, payload: camelCasedObjectToSnakeCasedObject(response) })
      notify('success', 'User activated')
      refetchUser()
      intercomConnection.trackEvent('user_marked_active', {})
    },
    onError (error) {
      const errors = handleError(error)
      dispatch({ type: RECEIVE_HAULER_TEAM_MEMBER_ACTIVATE_FAILURE, payload: { errors } })
    }
  })

  const { mutate: deactivateUserMutation } = useMutation(deactivateUser, {
    onSuccess (response) {
      response.user = camelCasedObjectToSnakeCasedObject(response.user)
      dispatch({ type: RECEIVE_HAULER_TEAM_MEMBER_DEACTIVATE_SUCCESS, payload: camelCasedObjectToSnakeCasedObject(response) })
      notify('success', 'User Deactivated')
      refetchUser()
      intercomConnection.trackEvent('user_marked_inactive', {})
    },
    onError (error) {
      const errors = handleError(error)
      dispatch({ type: RECEIVE_HAULER_TEAM_MEMBER_DEACTIVATE_FAILURE, payload: { errors } })
    }
  })

  const handleEditTeamMemberProfile = useCallback(() => {
    history.push(`/hauler/team/${id}/edit`)
  }, [history, id])

  const fetchTeamMemberNotifications = useCallback((page = 1, size = 25) => {
    notificationsQuery({
      dataLoaders,
      queryKey: [
        QUERY_KEYS.notifications,
        {
          userId: user.id,
          filters: {
            receivingUserId: id
          },
          first: page === 1 ? size : null,
          after: page > 1 ? size * (page - 1) : null
        }
      ]
    })
      .then((response) => {
        dispatch({
          type: RECEIVE_CURRENT_HAULER_TEAM_MEMBER_NOTIFICATIONS_SUCCESS,
          payload: {
            notifications: response.notifications.nodes,
            pagination: response.notifications.pageInfo
          }
        })
      })
      .catch((error) => {
        const errors = handleError(error)
        dispatch({ type: RECEIVE_CURRENT_HAULER_TEAM_MEMBER_NOTIFICATIONS_FAILURE, payload: { errors } })
      })
  }, [dataLoaders, dispatch, id, user])

  const onPageRequest = (page = 1) => {
    fetchTeamMemberNotifications(page)
  }

  const activateHaulerTeamMember = () => {
    activateUserMutation({ userId: id, status: 'ACTIVATE' })
  }

  const deactivateHaulerTeamMember = () => {
    deactivateUserMutation({ userId: id })
  }

  useEffect(() => {
    fetchTeamMemberNotifications(1)
  }, [fetchTeamMemberNotifications])

  if (userListFetching) return <Spinner isFetching />

  const userData = selfUser ? user : users?.nodes[0]

  return (
    <div>
      <NormalLayoutContainer showBackLink>
        <div className='container-fluid'>
          <PageTitle title={`${userData.firstName} ${userData.lastName}`}>
            <button
              className='btn btn-lg btn-primary-dk pull-right push-down-super-small space-left'
              onClick={handleEditTeamMemberProfile}>
              <span className='pull-left'>Edit Profile</span>
              <i className='material-icons pull-right thumbster-button-icon'>mode_edit</i>
            </button>

            {(() => {
              if (selfUser) return null
              if (userData.deletedAt) {
                return (<button
                  className='btn btn-lg btn-primary pull-right push-down-super-small space-left'
                  onClick={activateHaulerTeamMember}>
                  <span className='pull-left'>Activate User</span>
                  <i className='material-icons pull-right thumbster-button-icon'>check</i>
                </button>)
              }
              return (<button
                className='btn btn-lg btn-danger pull-right push-down-super-small'
                onClick={deactivateHaulerTeamMember}>
                <span className='pull-left'>Deactivate User</span>
                <i className='material-icons pull-right thumbster-button-icon'>block</i>
              </button>)
            })()}

            <button
              className='btn btn-lg btn-link pull-right push-down-super-small space-right'
              onClick={() => history.push('/hauler/team/new/after-add-team-member')}>
              Onboarding Instructions
            </button>
          </PageTitle>
          <br />

          <HaulerTeamMemberDisplay teamMember={userData} />

          <Spinner isFetching={currentHaulerTeamMemberNotifications.isFetching}>
            <div className='push-down-very-small row'>
              <NotificationsReadOnlyList
                title={`Notifications sent to ${userData.firstName}`}
                notifications={currentHaulerTeamMemberNotifications.notifications}
                pagination={currentHaulerTeamMemberNotifications.pagination}
                onPageRequest={onPageRequest}
                includeLink
              />
            </div>
          </Spinner>
        </div>
      </NormalLayoutContainer>
    </div>
  )
}
